import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, FormControlLabel, InputLabel, MenuItem, Select, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Typography, Paper
} from '@mui/material';
import { PayrollService } from '../../services/payroll.service';
import { useToast } from '../../context/ToastContext';
import {
    IDisbursementSummaryRow, IRecordBatchRequest, ISalaryPaymentBatch, SalaryPaymentMethod
} from '../../interfaces/salaryPayment';
import { toLocalIsoDate } from '../../utils/dateUtils';
import FundingCheckPanel from '../compliance/FundingCheckPanel'; // W3-R
import { helpWhy } from '../../help/why';

export interface BatchPayrollRow {
    id: string;
    employeeName: string;
    netSalary: number;
    user?: {
        bankName?: string | null;
        bankAccountNumber?: string | null;
        mobileBankingProvider?: string | null;
        mobileBankingAccount?: string | null;
    };
}

interface Props {
    open: boolean;
    onClose: () => void;
    selectedPayrolls: BatchPayrollRow[];
    onSuccess: (batch: ISalaryPaymentBatch) => void;
    // MFS disbursement lane (spec §2, "BatchPaymentDialog.tsx"): when all three are supplied, the
    // dialog fetches the period's preferred-disbursement summary and offers channel batching.
    // Optional so this dialog still works standalone (e.g. existing callers/tests) without them.
    institutionId?: string;
    year?: number;
    month?: number;
}

const METHODS: SalaryPaymentMethod[] = ['CASH', 'BANK_TRANSFER', 'MOBILE_BANKING', 'CHEQUE', 'OTHER'];

const todayIso = () => toLocalIsoDate();

const isMissing = (v?: string | null) => !v || v.trim() === '';

const BatchPaymentDialog: React.FC<Props> = ({
    open, onClose, selectedPayrolls, onSuccess, institutionId, year, month
}) => {
    const { showSuccess, showApiError } = useToast();
    const [batchDate, setBatchDate] = useState<string>(todayIso());
    const [paymentMethod, setPaymentMethod] = useState<SalaryPaymentMethod>('CASH');
    const [reference, setReference] = useState<string>('');
    const [notes, setNotes] = useState<string>('');
    const [reason, setReason] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);

    // MFS disbursement lane: channel-batching aids, gated on having a period to summarize.
    const channelBatchingEnabled = Boolean(institutionId && year && month);
    const [disbursementSummary, setDisbursementSummary] = useState<IDisbursementSummaryRow[]>([]);
    const [onlyPreferredMethod, setOnlyPreferredMethod] = useState(false);
    const [skippedPayrollIds, setSkippedPayrollIds] = useState<string[] | null>(null);
    // Fix round 1 / HIGH: `selectedPayrolls` is a prop the parent can clear the moment `onSuccess`
    // fires (PayrollList.handleBatchSuccess does exactly that in the same render pass), so
    // resolving skipped names from the LIVE prop after that point shows raw UUIDs instead of
    // names. Snapshot the id->name map at submit time and resolve the notice from the snapshot,
    // never from the live prop.
    const submittedNamesByIdRef = useRef<Record<string, string>>({});

    useEffect(() => {
        if (open) {
            setBatchDate(todayIso());
            setPaymentMethod('CASH');
            setReference('');
            setNotes('');
            setReason('');
            setSkippedPayrollIds(null);
            setDisbursementSummary([]);
            setOnlyPreferredMethod(false);
            if (institutionId && year && month) {
                PayrollService.getDisbursementSummary(institutionId, year, month)
                    .then((res) => {
                        setDisbursementSummary(res.rows);
                        // Default ON when at least one employee in the month carries a preference
                        // (spec §2, "BatchPaymentDialog.tsx").
                        const hasPreference = res.rows.some(
                            (row) => row.method !== 'UNSPECIFIED' && row.count > 0
                        );
                        setOnlyPreferredMethod(hasPreference);
                    })
                    .catch(() => setDisbursementSummary([]));
            }
        }
    }, [open, institutionId, year, month]);

    const totalAmount = useMemo(
        () => selectedPayrolls.reduce((sum, p) => sum + (Number(p.netSalary) || 0), 0),
        [selectedPayrolls]
    );

    const missingInfoCount = useMemo(() => {
        if (paymentMethod === 'BANK_TRANSFER') {
            return selectedPayrolls.filter(p =>
                isMissing(p.user?.bankName) || isMissing(p.user?.bankAccountNumber)
            ).length;
        }
        if (paymentMethod === 'MOBILE_BANKING') {
            return selectedPayrolls.filter(p =>
                isMissing(p.user?.mobileBankingProvider) || isMissing(p.user?.mobileBankingAccount)
            ).length;
        }
        return 0;
    }, [selectedPayrolls, paymentMethod]);

    const bankingUnavailable = selectedPayrolls.some(p => p.user === undefined)
        && (paymentMethod === 'BANK_TRANSFER' || paymentMethod === 'MOBILE_BANKING');

    const handleSubmit = async () => {
        if (selectedPayrolls.length === 0) return;
        // Snapshot BEFORE the await — selectedPayrolls is guaranteed live at this point, unlike
        // after onSuccess() below has had a chance to let the parent re-render with a cleared
        // selection.
        submittedNamesByIdRef.current = Object.fromEntries(
            selectedPayrolls.map((p) => [p.id, p.employeeName])
        );
        const req: IRecordBatchRequest = {
            batchDate,
            paymentMethod,
            reference: reference.trim() || null,
            payrollIds: selectedPayrolls.map(p => p.id),
            notes: notes.trim() || null,
            reason: reason.trim() || null,
            onlyPreferredMethod: channelBatchingEnabled ? onlyPreferredMethod : undefined,
        };
        setSubmitting(true);
        try {
            const batch = await PayrollService.recordBatch(req);
            showSuccess(`Batch payment recorded for ${batch.completedPayments} payroll(s)`);
            onSuccess(batch);
            // Never silent: if the "only preferred method" filter dropped any payroll ids, keep
            // the dialog open to show which employees were skipped instead of closing it (spec
            // §2, "show skipped ids after creation").
            if (batch.skippedPayrollIds && batch.skippedPayrollIds.length > 0) {
                setSkippedPayrollIds(batch.skippedPayrollIds);
            } else {
                onClose();
            }
        } catch (error) {
            showApiError(error, 'Failed to record batch payment');
        } finally {
            setSubmitting(false);
        }
    };

    const skippedNames = useMemo(
        () => (skippedPayrollIds ?? []).map(
            (id) => submittedNamesByIdRef.current[id] || id
        ),
        [skippedPayrollIds]
    );

    const renderPreview = () => {
        if (paymentMethod === 'BANK_TRANSFER') {
            return (
                <TableContainer component={Paper} variant="outlined" data-help-section="payroll.batchPay.preview">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="payroll.batchPay.col.employee">Employee</TableCell>
                                <TableCell align="right" data-help="payroll.batchPay.col.net">Net Salary</TableCell>
                                <TableCell data-help="payroll.batchPay.col.bankName">Bank Name</TableCell>
                                <TableCell data-help="payroll.batchPay.col.bankAccount">Bank Account</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {selectedPayrolls.map(p => {
                                const missing = isMissing(p.user?.bankName) || isMissing(p.user?.bankAccountNumber);
                                return (
                                    <TableRow
                                        key={p.id}
                                        sx={missing ? { backgroundColor: 'warning.light' } : undefined}
                                        data-testid={missing ? 'row-missing' : 'row-ok'}
                                    >
                                        <TableCell>{p.employeeName}</TableCell>
                                        <TableCell align="right">{Number(p.netSalary).toFixed(2)}</TableCell>
                                        <TableCell>{p.user?.bankName || <em>missing</em>}</TableCell>
                                        <TableCell>{p.user?.bankAccountNumber || <em>missing</em>}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        }
        if (paymentMethod === 'MOBILE_BANKING') {
            return (
                <TableContainer component={Paper} variant="outlined" data-help-section="payroll.batchPay.preview">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="payroll.batchPay.col.employee">Employee</TableCell>
                                <TableCell align="right" data-help="payroll.batchPay.col.net">Net Salary</TableCell>
                                <TableCell data-help="payroll.batchPay.col.provider">Provider</TableCell>
                                <TableCell data-help="payroll.batchPay.col.account">Account</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {selectedPayrolls.map(p => {
                                const missing = isMissing(p.user?.mobileBankingProvider) || isMissing(p.user?.mobileBankingAccount);
                                return (
                                    <TableRow
                                        key={p.id}
                                        sx={missing ? { backgroundColor: 'warning.light' } : undefined}
                                        data-testid={missing ? 'row-missing' : 'row-ok'}
                                    >
                                        <TableCell>{p.employeeName}</TableCell>
                                        <TableCell align="right">{Number(p.netSalary).toFixed(2)}</TableCell>
                                        <TableCell>{p.user?.mobileBankingProvider || <em>missing</em>}</TableCell>
                                        <TableCell>{p.user?.mobileBankingAccount || <em>missing</em>}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        }
        return (
            <TableContainer component={Paper} variant="outlined" data-help-section="payroll.batchPay.preview">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="payroll.batchPay.col.employee">Employee</TableCell>
                            <TableCell align="right" data-help="payroll.batchPay.col.net">Net Salary</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {selectedPayrolls.map(p => (
                            <TableRow key={p.id}>
                                <TableCell>{p.employeeName}</TableCell>
                                <TableCell align="right">{Number(p.netSalary).toFixed(2)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'payroll.batchPay.page' } as any} maxWidth="md" fullWidth>
            <DialogTitle>Record Batch Payment</DialogTitle>
            <DialogContent dividers data-help-section="payroll.batchPay.section.details">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                            data-help="payroll.batchPay.date"
                            label="Batch Date"
                            type="date"
                            value={batchDate}
                            onChange={(e) => setBatchDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            required
                            sx={{ flex: '1 1 200px' }}
                        />
                        <FormControl data-help="payroll.batchPay.method" required sx={{ flex: '1 1 200px' }}>
                            <InputLabel id="batch-method-label">Payment Method</InputLabel>
                            <Select
                                labelId="batch-method-label"
                                label="Payment Method"
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value as SalaryPaymentMethod)}
                                inputProps={{ 'aria-label': 'Payment Method' }}
                            >
                                {METHODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField
                            data-help="payroll.batchPay.reference"
                            label="Reference"
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            sx={{ flex: '1 1 200px' }}
                            helperText="Common batch reference (optional)"
                        />
                    </Box>

                    <TextField
                        data-help="payroll.batchPay.notes"
                        label="Notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        multiline
                        rows={2}
                        fullWidth
                    />
                    <TextField
                        data-help="payroll.batchPay.reason"
                        label="Reason (required if any payment overpays)"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        fullWidth
                        helperText="Only needed if a selected payroll is already partly/fully paid"
                    />

                    {bankingUnavailable && (
                        <Alert severity="info">
                            Banking info is not available on the payroll list yet. Preview is degraded;
                            backend will accept blanks and payments will still be recorded.
                        </Alert>
                    )}
                    {missingInfoCount > 0 && !bankingUnavailable && (
                        <Alert severity="warning">
                            {missingInfoCount} of {selectedPayrolls.length} rows are missing{' '}
                            {paymentMethod === 'BANK_TRANSFER' ? 'bank info' : 'mobile banking info'}.
                            Payments will still be recorded.
                        </Alert>
                    )}

                    {channelBatchingEnabled && disbursementSummary.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Preferred disbursement this period
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                {disbursementSummary.map((row) => (
                                    <Chip
                                        key={row.method}
                                        size="small"
                                        label={`${row.method}: ${row.count} (net ${Number(row.netTotal).toFixed(2)})`}
                                        color={row.method === paymentMethod ? 'primary' : 'default'}
                                        variant={row.method === paymentMethod ? 'filled' : 'outlined'}
                                    />
                                ))}
                            </Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        data-help="payroll.batchPay.onlyPreferred"
                                        checked={onlyPreferredMethod}
                                        onChange={(e) => setOnlyPreferredMethod(e.target.checked)}
                                    />
                                }
                                label="Only employees whose preferred method matches"
                            />
                        </Box>
                    )}

                    {skippedPayrollIds && skippedPayrollIds.length > 0 && (
                        <Alert severity="info">
                            {skippedPayrollIds.length} payroll(s) skipped — preferred disbursement method
                            did not match {paymentMethod}: {skippedNames.join(', ')}
                        </Alert>
                    )}

                    <Typography variant="subtitle2">
                        {selectedPayrolls.length} payroll(s) &nbsp;|&nbsp;
                        Total: {totalAmount.toFixed(2)}
                    </Typography>

                    {renderPreview()}
                    {/* W3-R: advisory funding check, never blocks */}
                    <FundingCheckPanel institutionId={institutionId} payrollIds={selectedPayrolls.map(p => p.id)} />
                </Box>
            </DialogContent>
            <DialogActions>
                {skippedPayrollIds && skippedPayrollIds.length > 0 ? (
                    <Button data-help="payroll.batchPay.close" onClick={onClose} variant="contained">Close</Button>
                ) : (
                    <>
                        <Button data-help="payroll.batchPay.cancel" onClick={onClose} disabled={submitting}>Cancel</Button>
                        <Button
                            data-help="payroll.batchPay.record"
                            {...helpWhy(submitting ? { kind: 'status', message: 'Recording is in progress.' } : selectedPayrolls.length === 0 ? { kind: 'status', message: 'Select at least one payroll first.' } : null)}
                            onClick={handleSubmit}
                            variant="contained"
                            disabled={submitting || selectedPayrolls.length === 0}
                        >
                            {submitting ? 'Recording...' : `Record ${selectedPayrolls.length} Payment(s)`}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default BatchPaymentDialog;
